import { pageTitle } from '@palaccount/config';
import {
  AUDIT_ACTIONS,
  AUDIT_PAGE_SIZE,
  AUDIT_VIEWS,
  type AuditAction,
  type AuditEntry,
  type AuditView,
} from '@palaccount/contracts';
import {
  CheckCircle2,
  Download,
  Lock,
  LogIn,
  Pencil,
  PlusCircle,
  Search,
  SearchX,
  ShieldCheck,
  Trash2,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { downloadCsv } from '@/features/reports/export';
import { ReportsTabs } from '@/features/reports/reports-tabs';
import { usePagedState } from '@/lib/paging';
import { cn } from '@/lib/utils';

import { useAuditExport, useAuditLog } from './api';
import { labelKeyForAuditEntity, routeForAuditEntity } from './audit-entity';
import { fieldLabel } from './field-label';

/**
 * DSH-09 — THE AUDIT LOG.
 *
 * NFR-25 — "there is currently no screen for the log… one must be built: a
 * filtered, searchable list by person, by date, by kind of record, and by
 * record." This page is that screen: the filter bar carries person, date
 * range, action and record-kind selects plus free search, all applied
 * server-side in audit-query.service.ts.
 *
 * "Every other screen in this product creates or reads business records. This
 * one records the act of creating and changing them."
 *
 * EC-19-60 — the prototype's retention-period box that NOTHING read is
 * resolved by its removal: no retention control exists on this screen or in
 * the settings registry, which is the rule's own "either wire it or remove
 * it". If archiving is ever wanted it arrives as a designed feature, not a
 * decorative box.
 *
 * READ-ONLY, and the absences are requirements: no Edit, no Delete, no New, no
 * **Run demo log call** (§5i: "a development aid that must not ship — a button
 * whose only purpose is to write a fake entry into an immutable record has no
 * place in a delivered product"). The one control that leaves a mark is Export
 * CSV, which writes its own Export entry (BR-DSH-09-21).
 *
 * THE BANNER IS NOT DISMISSIBLE and is not conditional on anything
 * (BR-DSH-09-22). "Whatever else a screen does, the promise that entries can
 * never be edited or deleted is not a detail somebody may switch off."
 */

/** §5b — each action's icon and colour, on a 14% tint of the same colour. */
const ACTION_STYLE: Record<AuditAction, { icon: LucideIcon; className: string }> = {
  create: { icon: PlusCircle, className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  update: { icon: Pencil, className: 'bg-blue-500/10 text-blue-700 dark:text-blue-400' },
  delete: { icon: Trash2, className: 'bg-red-500/10 text-red-700 dark:text-red-400' },
  approve: { icon: CheckCircle2, className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  reject: { icon: XCircle, className: 'bg-red-500/10 text-red-700 dark:text-red-400' },
  // "Login is grey, because signing in is not an event in itself — only a
  // FAILED one matters, and that is carried in the change row."
  login: { icon: LogIn, className: 'bg-muted text-muted-foreground' },
  export: { icon: Download, className: 'bg-amber-500/10 text-amber-700 dark:text-amber-400' },
  // "Permission Change has its own colour, distinct from the five semantic
  // colours used everywhere else in the product, because it is the one action
  // that changes what everybody else is allowed to do."
  permission_change: {
    icon: ShieldCheck,
    className: 'bg-purple-500/10 text-purple-700 dark:text-purple-400',
  },
};

/**
 * BR-DSH-09-12 — "stored in UTC and displayed in the reader's local time",
 * as `MMM D, HH:MM` (§5a field 2).
 *
 * The READER's local time, and deliberately not the company clock that every
 * other date on every other screen uses. It is the one place the difference is
 * right: an investigator is asking "was I at my desk when this happened?", and
 * the answer has to be in the clock on their own wall.
 */
function formatStamp(iso: string, locale: string): string {
  return new Date(iso).toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function initials(name: string | null, email: string | null): string {
  const source = (name ?? email ?? '?').trim();
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function AuditLogPage() {
  const { t, i18n } = useTranslation();

  const [view, setView] = useState<AuditView>('global');
  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState<AuditAction | ''>('');
  const [entityType, setEntityType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  /**
   * BR-DSH-09-19 — "Changing any filter or tab must return to the first page."
   *
   * This screen was the product's first pagination and it kept its page in
   * component state, which meant a link to "page 3 of the audit log" did not
   * exist and a refresh returned to page 1. It now uses the shared hook it
   * asked for in PAGINATION.md, so the page lives in the URL like every other
   * paged screen — and the reset-through-the-setters rule (never an effect on
   * the filter values; an effect fires on first render and would throw away
   * the page the URL just asked for) lives in one place instead of forty.
   */
  const paging = usePagedState(AUDIT_PAGE_SIZE);
  const page = paging.page;

  useEffect(() => {
    document.title = pageTitle(t('audit.title'));
  }, [t]);

  /** The shared reset, plus this screen's own detail panel closing with it. */
  function change<T>(setter: (value: T) => void): (value: T) => void {
    return (value) => {
      paging.change(setter)(value);
      setOpen(null);
    };
  }

  const query = useMemo(
    () => ({
      view,
      page,
      pageSize: AUDIT_PAGE_SIZE,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(userId ? { userId } : {}),
      ...(action ? { action } : {}),
      ...(entityType ? { entityType } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    }),
    [view, page, search, userId, action, entityType, from, to],
  );

  // The User-activity view is scoped to one person and needs one chosen; the
  // API refuses the query otherwise, so the screen does not send it.
  const ready = view !== 'user' || userId !== '';
  const { data, isPending, isError } = useAuditLog(ready ? query : { ...query, view: 'global' });
  const exportCsv = useAuditExport();

  const entries = ready ? (data?.entries ?? []) : [];
  const total = ready ? (data?.total ?? 0) : 0;
  const pages = Math.max(1, Math.ceil(total / AUDIT_PAGE_SIZE));
  const firstRow = total === 0 ? 0 : (page - 1) * AUDIT_PAGE_SIZE + 1;
  const lastRow = Math.min(page * AUDIT_PAGE_SIZE, total);

  async function onExport() {
    const { view: v, page: _p, pageSize: _s, ...rest } = query;
    const result = await exportCsv.mutateAsync({ view: v, ...rest });
    // The server built the file (it is the same rows the log will record it
    // exported); the browser only saves it, with the BOM Excel needs for
    // Arabic.
    const [headerLine, ...rows] = result.csv.split('\r\n');
    downloadCsv(
      'audit-log.csv',
      (headerLine ?? '').split(','),
      rows.filter(Boolean).map((line) => [line]),
    );
  }

  return (
    <div className="grid gap-4">
      <ReportsTabs />

      {/* §1 — the banner the user cannot dismiss (BR-DSH-09-22). */}
      <p
        role="note"
        className="flex items-start gap-2.5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
      >
        <Lock className="mt-0.5 size-4 shrink-0" aria-hidden />
        {t('audit.immutable')}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-bold">{t('audit.title')}</h2>
        {/* §5d — three tabs, all three open to anybody who may open the
            screen (BR-PERM-52: "Anybody holding the grant gets them; nothing
            else may narrow that"). */}
        <div className="flex gap-1" role="tablist" aria-label={t('audit.views')}>
          {AUDIT_VIEWS.map((candidate) => (
            <button
              key={candidate}
              type="button"
              role="tab"
              aria-selected={view === candidate}
              onClick={() => change(setView)(candidate)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground',
                view === candidate && 'bg-accent text-foreground',
              )}
            >
              {t(`audit.view.${candidate}`)}
            </button>
          ))}
        </div>
        <div className="ms-auto">
          <Button variant="outline" onClick={() => void onExport()} disabled={exportCsv.isPending}>
            <Download aria-hidden /> {t('audit.exportCsv')}
          </Button>
        </div>
      </div>

      {exportCsv.isSuccess ? (
        <p className="text-sm text-muted-foreground" role="status">
          {t('audit.exported', { count: exportCsv.data.exported })}
        </p>
      ) : null}

      {/* §5e — six filters, applying at once, no Apply button. */}
      <Card>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <label className="relative grid gap-1.5 text-sm">
            <span className="font-medium">{t('audit.search')}</span>
            <Search
              className="pointer-events-none absolute bottom-3.5 start-3 size-4 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(event) => change(setSearch)(event.target.value)}
              placeholder={t('audit.searchPlaceholder')}
              className="ps-9"
            />
          </label>

          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">{t('audit.user')}</span>
            <NativeSelect value={userId} onChange={(event) => change(setUserId)(event.target.value)}>
              <option value="">{t('audit.allUsers')}</option>
              {(data?.users ?? []).map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </NativeSelect>
          </label>

          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">{t('audit.action')}</span>
            <NativeSelect
              value={action}
              disabled={view === 'security'}
              onChange={(event) => change(setAction)(event.target.value as AuditAction | '')}
            >
              <option value="">{t('audit.allActions')}</option>
              {AUDIT_ACTIONS.map((candidate) => (
                <option key={candidate} value={candidate}>
                  {t(`audit.action.${candidate}`)}
                </option>
              ))}
            </NativeSelect>
          </label>

          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">{t('audit.entity')}</span>
            {/* BR-DSH-09-15 — only the types that appear in the log, so the
                list never offers a choice that returns nothing. */}
            <NativeSelect
              value={entityType}
              onChange={(event) => change(setEntityType)(event.target.value)}
            >
              <option value="">{t('audit.allEntities')}</option>
              {(data?.entityTypes ?? []).map((type) => (
                <option key={type} value={type}>
                  {/* The same key the table cell uses, so the filter and the
                      column cannot call one type two things one control apart
                      (ruling D5's rule, applied to a word instead of a
                      number). */}
                  {t(labelKeyForAuditEntity(type), { defaultValue: type })}
                </option>
              ))}
            </NativeSelect>
          </label>

          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">{t('audit.from')}</span>
            <Input
              type="date"
              dir="ltr"
              value={from}
              onChange={(event) => change(setFrom)(event.target.value)}
            />
          </label>

          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">{t('audit.to')}</span>
            <Input
              type="date"
              dir="ltr"
              value={to}
              onChange={(event) => change(setTo)(event.target.value)}
            />
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          {!ready ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">
              {t('audit.chooseUser')}
            </p>
          ) : isError ? (
            <p role="alert" className="px-4 py-10 text-center text-sm text-destructive">
              {t('common.loadFailed')}
            </p>
          ) : isPending ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground" aria-busy>
              {t('common.loading')}
            </p>
          ) : entries.length === 0 ? (
            /*
              ERR-DSH-09-01 / ERR-DSH-09-07 — "Filtered down to nothing: a
              large search-off icon and `No entries match these filters.`"
              An empty result here is never a fault: an audit log that returns
              nothing for a narrow filter is the log working, and §14 asks for
              a line rather than an error for exactly that reason. Compare the
              `isError` arm three lines up, which carries role="alert" —
              the two states are told apart on purpose, because a reader who
              cannot tell "no matches" from "the query failed" will widen the
              filters and conclude there is nothing to find.

              The SENTENCE is the spec's, verbatim. It read "Nothing matches
              these filters." — the same meaning in different words, which is
              exactly the drift a literal message rule exists to stop: a
              paraphrase is untestable against the document and the next one
              paraphrases the paraphrase. The search-off icon is §14's too,
              and it was simply absent.
            */
            <p className="flex flex-col items-center gap-2 px-4 py-10 text-center text-sm text-muted-foreground">
              <SearchX className="h-8 w-8" aria-hidden />
              {t('audit.empty')}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('audit.colTimestamp')}</TableHead>
                  <TableHead>{t('audit.colUser')}</TableHead>
                  <TableHead>{t('audit.colAction')}</TableHead>
                  <TableHead>{t('audit.colEntity')}</TableHead>
                  <TableHead>{t('audit.colChanges')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <AuditRow
                    key={entry.id}
                    entry={entry}
                    locale={i18n.language}
                    open={open === entry.id}
                    onToggle={() => setOpen(open === entry.id ? null : entry.id)}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* §5f — the range, the total, and the two steps. BR-DSH-09-18. */}
      <Pager
        page={page}
        pages={pages}
        first={firstRow}
        last={lastRow}
        total={total}
        onPage={paging.setPage}
        label={t('audit.title')}
        onLeave={() => setOpen(null)}
      />

      {/*
        NFR-42 — PAGINATION.md beside this file is the countdown: 80 list
        routes, 7 paged, 73 to go, in the order a tenant will feel them.
      */}
    </div>
  );
}

/**
 * The entity's name — a link where this build knows where the record opens,
 * and plain text where it does not.
 *
 * The destination comes from `audit-entity.ts`, which is total over the roster
 * and returns null for anything outside it, so there is no branch here that can
 * draw a link to a path nobody chose.
 */
function AuditEntityName({ entry }: { entry: AuditEntry }) {
  const { t } = useTranslation();
  const type = entry.entityType;
  if (type === null) return null;

  const name = t(labelKeyForAuditEntity(type), { defaultValue: type });
  const route = routeForAuditEntity(entry);
  if (route === null) {
    return <span className="block text-sm">{name}</span>;
  }
  return (
    <Link to={route} className="block text-sm text-primary underline-offset-2 hover:underline">
      {name}
    </Link>
  );
}

/**
 * One row. Top-aligned rather than centred, "because the field-changes column
 * can run to many lines while the others are one line" (§4).
 */
function AuditRow({
  entry,
  locale,
  open,
  onToggle,
}: {
  entry: AuditEntry;
  locale: string;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const style = ACTION_STYLE[entry.action];
  const Icon = style.icon;

  return (
    <>
      <TableRow className="align-top">
        <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
          <Ltr value={formatStamp(entry.occurredAt, locale)} />
        </TableCell>
        <TableCell>
          <span className="flex items-center gap-2">
            <span
              aria-hidden
              className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold"
            >
              {initials(entry.actorName, entry.actorEmail)}
            </span>
            {/* BR-DSH-09-14: the row stays readable after the person's record
                is gone — the snapshotted email is what still identifies them. */}
            <span className="min-w-0 truncate">
              {entry.actorName ?? entry.actorEmail ?? t('audit.systemActor')}
            </span>
          </span>
        </TableCell>
        <TableCell>
          <span
            className={cn(
              'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold',
              style.className,
            )}
          >
            <Icon className="size-3.5" aria-hidden />
            {t(`audit.action.${entry.action}`)}
          </span>
          {/* The stored event beside the chip: "Update / Invoice" is a fact,
              `invoice.post` is the answer. */}
          <span className="mt-1 block font-mono text-[10px] text-muted-foreground">
            <Ltr value={entry.event} />
          </span>
        </TableCell>
        <TableCell>
          {entry.entityType ? (
            <>
              {/*
                §21.13 — "THE ENTITY LINK OPENS THE RECORD IT NAMES."

                The NAME is the link, and the id below it stays the detail
                toggle. Two affordances, two answers: "take me to the invoice"
                and "show me the address this came from". Making the id the link
                would have taken the detail panel's only control away, and §5a's
                fields 8–10 have nowhere else to be drawn.

                THREE STATES, and they are deliberately different sentences:

                  • a type the roster routes → a link (`AuditEntityName`);
                  • a type the roster PINS NULL, or one it does not know at all
                    because the row predates a rename → PLAIN TEXT. Never a dead
                    link: a link that goes nowhere teaches a reader the product
                    is broken, and one that goes somewhere WRONG is worse,
                    because it looks identical to a right one;
                  • no entity at all → the em dash below.

                ERR-DSH-09-02 / ERR-DSH-09-08 — "Clicked the id on a Session row:
                a short message saying there is no linked view for that type",
                because "a sign-in has no record to open." This build still never
                has to say it, and the reason is still structural: `auth.signin`
                writes NO target type (auth.service.ts:345/529), so a Session row
                takes the em-dash branch and has nothing to click in the first
                place. The message would apologise for a promise the screen does
                not make.
              */}
              <AuditEntityName entry={entry} />
              {entry.entityId ? (
                <button
                  type="button"
                  onClick={onToggle}
                  className="font-mono text-[11px] text-primary hover:underline"
                  aria-expanded={open}
                >
                  <Ltr value={entry.entityId.slice(0, 8)} />
                </button>
              ) : null}
            </>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="min-w-[16rem]">
          {entry.changes.length === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <ul className="grid gap-0.5 text-xs">
              {entry.changes.map((change, index) => (
                <li key={`${change.field}-${index}`}>
                  {/* §5g — field name bold grey, old value red struck through,
                      arrow, new value green and bold; an em dash for empty.
                      NFR-04: the arrow points along the reading direction, so
                      it is dictionary text (`unit.arrow`) — ← in Arabic — not
                      a keyboard glyph that would point backwards in RTL. */}
                  <span className="font-semibold text-muted-foreground">
                    {fieldLabel(t, change.field)}:
                  </span>{' '}
                  <span className="text-red-700 line-through dark:text-red-400">
                    {change.from ?? '—'}
                  </span>{' '}
                  <span aria-hidden>{t('unit.arrow')}</span>{' '}
                  <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                    {change.to ?? '—'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </TableCell>
      </TableRow>
      {open ? (
        <TableRow>
          <TableCell colSpan={5} className="bg-muted/40 text-xs">
            {/* §5a: fields 8–10 are "stored but not shown in the table", with
                a proposed default of a detail panel. Adopted — "they are the
                first things asked for after a suspected intrusion, so storing
                them without a way to read them would waste them." */}
            <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-3">
              <div>
                <dt className="font-semibold text-muted-foreground">{t('audit.detailId')}</dt>
                <dd className="font-mono">
                  <Ltr value={entry.entityId ?? '—'} />
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-muted-foreground">{t('audit.detailIp')}</dt>
                <dd className="font-mono">
                  <Ltr value={entry.ip ?? '—'} />
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-muted-foreground">{t('audit.detailOutcome')}</dt>
                <dd>
                  {t(`audit.outcome.${entry.outcome}`)}
                  {entry.reason ? ` — ${entry.reason}` : ''}
                </dd>
              </div>
              <div className="sm:col-span-3">
                <dt className="font-semibold text-muted-foreground">{t('audit.detailEntry')}</dt>
                <dd className="font-mono">
                  <Ltr value={entry.id} />
                </dd>
              </div>
            </dl>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
